import { extend, isEmpty } from 'lodash';

const SELECTOR = '.js-audio-player';

function AudioPlayer(container, options = {}) {
    this.container = container;

    this.options = extend({}, {
        progressBarSelector: options.progressBarSelector || '.js-audio-player__progress-bar',
        durationElapsedSelector: options.durationElapsedSelector || '.js-audio-player__duration-elapsed',
        durationRemainSelector: options.durationRemainSelector || '.js-audio-player__duration-remain',
        audioSelector: options.audioSelector || '.js-audio-player__player',
        previousSelector: options.previousSelector || '.js-audio-player__previous',
        playPauseSelector: options.playPauseSelector || '.js-audio-player__play-pause',
        nextSelector: options.nextSelector || '.js-audio-player__next',
        muteUnmuteSelector: options.muteUnmuteSelector || '.js-audio-player__mute-unmute',
        volumeSelector: options.volumeSelector || '.js-audio-player__volume',
        itemSelector: options.itemSelector || '.js-audio-player__item',
        isPlayingClass: options.isPlayingClass || 'is-playing',
        isActiveClass: options.isActiveClass || 'is-active',
        isMutedClass: options.isMutedClass || 'is-muted',
    });

    this.player = container.querySelector(this.options.audioSelector);

    this.controls = {
        progressBar: container.querySelector(this.options.progressBarSelector),
        playPause: container.querySelector(this.options.playPauseSelector),
        next: container.querySelector(this.options.nextSelector),
        previous: container.querySelector(this.options.previousSelector),
        muteUnmute: container.querySelector(this.options.muteUnmuteSelector),
        volume: container.querySelector(this.options.volumeSelector),
    }

    this.display = {
        durationElapsed: container.querySelector(this.options.durationElapsedSelector),
        durationRemain: container.querySelector(this.options.durationRemainSelector),
    }

    if ('localStorage' in window && 'audioPlayerVolume' in window.localStorage) {
        this.player.volume = parseFloat(window.localStorage.audioPlayerVolume);
    } else {
        this.player.volume = 0.7;
    }

    if (this.controls.volume) {
        this.controls.volume.value = this.player.volume;
    }

    this.playing = false;
    this.item = 0;
    this.playlist = [];
    this.muteVolume = null;
    this.isMuting = false;

    for (const [ index, item ] of container.querySelectorAll(this.options.itemSelector).entries()) {
        if ('src' in item.dataset === false || isEmpty(item.dataset.src)) {
            continue;
        }

        if (index === this.item) {
            item.classList.add(this.options.isActiveClass);
        }

        this.playlist.push({
            element: item,
            source: item.dataset.src,
        });
    }

    this.setupListeners();
    this.loadTrack();
}

AudioPlayer.prototype.parseDuration = function (seconds) {
    if (seconds > 3600) {
        return new Date(seconds * 1000).toISOString().slice(11, 19);
    }

    return new Date(seconds * 1000).toISOString().slice(14, 19);
}

AudioPlayer.prototype.setupListeners = function () {
    this.player.addEventListener('canplaythrough', event => {
        this.updateDuration(event);
        this.updateProgressBar(event);
    });

    this.player.addEventListener('timeupdate', event => {
        this.updateDuration(event);
        this.updateProgressBar(event);
    });

    this.player.addEventListener('ended', () => {
        this.nextTrack(true);

        if (this.controls.playPause) {
            this.controls.playPause.classList.remove(this.options.isPlayingClass);
        }
    });

    this.player.addEventListener('play', () => {
        if (this.controls.playPause) {
            this.controls.playPause.classList.add(this.options.isPlayingClass);
        }
    });

    this.player.addEventListener('pause', () => {
        if (this.controls.playPause) {
            this.controls.playPause.classList.remove(this.options.isPlayingClass);
        }
    });

    this.player.addEventListener('volumechange', () => {
        if (this.controls.muteUnmute) {
            if (!this.player.volume || this.player.muted) {
                this.controls.muteUnmute.classList.add(this.options.isMutedClass);
            } else {
                this.controls.muteUnmute.classList.remove(this.options.isMutedClass);
            }
        }

        if (this.controls.volume) {
            this.controls.volume.value = this.player.volume;
        }

        if ('localStorage' in window) {
            window.localStorage.audioPlayerVolume = this.player.volume;
        }
    });

    if (this.controls.playPause) {
        this.controls.playPause.addEventListener('click', event => {
            event.preventDefault();
            this.playPause();
        });
    }

    if (this.controls.next) {
        this.controls.next.addEventListener('click', event => {
            event.preventDefault();
            this.nextTrack();
        });
    }

    if (this.controls.previous) {
        this.controls.previous.addEventListener('click', event => {
            event.preventDefault();
            this.previousTrack();
        });
    }

    if (this.controls.muteUnmute) {
        this.controls.muteUnmute.addEventListener('click', () => {
            if (this.player.muted) {
                this.player.volume = this.muteVolume;
                this.player.muted = false;
            } else {
                this.muteVolume = this.player.volume;
                this.player.volume = 0;
                this.player.muted = true;
            }
        });
    }

    if (this.controls.progressBar) {
        this.controls.progressBar.addEventListener('input', event => {
            this.player.currentTime = this.player.duration * parseFloat(event.currentTarget.value);
        });
    }

    if (this.controls.volume) {
        this.controls.volume.addEventListener('input', event => {
            this.player.volume = parseFloat(event.currentTarget.value);

            if (this.player.muted) {
                this.player.muted = false;
            }
        });
    }

    this.playlist.forEach((item, index) => {
        item.element.addEventListener('click', event => {
            event.preventDefault();
            this.item = index;
            this.loadTrack();
            this.playPause(true);
        });
    })
}

AudioPlayer.prototype.loadTrack = function () {
    this.player.pause();

    this.player.src = this.playlist[this.item].source;

    this.playlist.forEach((item, index) => {
        if (index === this.item) {
            item.element.classList.add(this.options.isActiveClass);
        } else {
            item.element.classList.remove(this.options.isActiveClass);
        }
    });
}

AudioPlayer.prototype.updateDuration = function (event) {
    const { duration, currentTime } = event.currentTarget;

    if (isNaN(duration) || isNaN(currentTime)) {
        return;
    }

    if (this.display.durationRemain) {
        this.display.durationRemain.textContent = '-' + this.parseDuration(duration - currentTime);
    }

    if (this.display.durationElapsed) {
        this.display.durationElapsed.textContent = this.parseDuration(currentTime)
    }
}

AudioPlayer.prototype.updateProgressBar = function (event) {
    const { duration, currentTime } = event.currentTarget;

    if (isNaN(duration) || isNaN(currentTime)) {
        return;
    }

    if (this.controls.progressBar) {
        this.controls.progressBar.value = currentTime / duration;
    }
}

AudioPlayer.prototype.playPause = function (force) {
    if (this.playing && !force) {
        this.player.pause();
        this.playing = false;
    } else {
        this.player.play();
        this.playing = true;
    }
}

AudioPlayer.prototype.nextTrack = function (auto) {
    this.item++;

    if (this.playlist[this.item] === undefined) {
        this.item = 0;
    }

    this.loadTrack();

    if (auto && this.item === 0) {
        this.playing = false;
    }

    if (this.playing) {
        this.player.play();
    }
}

AudioPlayer.prototype.previousTrack = function () {
    this.item--;

    if (this.playlist[this.item] === undefined) {
        this.item = this.playlist.length - 1;
    }

    this.loadTrack();

    if (this.playing) {
        this.player.play();
    }
}

export function init() {
    const elements = document.querySelectorAll(SELECTOR);

    elements.forEach(element => {
        new AudioPlayer(element);
    });
}



//     function setVolumeCSSClass() {
//         forEach(CSS_VOLUME_CLASSES, function(cssClass) {
//             $(controls.mute).removeClass(cssClass);
//         });

//         if (state.currentVolume <= 0) {
//             $(controls.mute).addClass(CSS_VOLUME_CLASSES[0]);
//         } else if (state.currentVolume <= 0.3) {
//             $(controls.mute).addClass(CSS_VOLUME_CLASSES[1]);
//         } else if (state.currentVolume <= 0.6) {
//             $(controls.mute).addClass(CSS_VOLUME_CLASSES[2]);
//         } else {
//             $(controls.mute).addClass(CSS_VOLUME_CLASSES[3]);
//         }
//     }


//     function formatCountPlayback(time) {
//         var date = new Date(null);
//         date.setSeconds(time); // specify value for SECONDS here
//         // TODO: Do we need to use moment here?
//         return moment.parseZone(date).format('mm:ss');
//     }



//     function nextAudio() {
//         var nextPos = ++state.currentPosition;
//         if (nextPos >= state.totalLength) {
//             state.currentPosition = 0;
//         } else {
//             state.currentPosition = nextPos;
//         }
//         loadAudio();
//     }

//     function prevAudio() {
//         if (audio.player.currentTime > 3) {
//             audio.player.currentTime = 0;
//             stopAudio();
//             audio.countPlaybackElement.html(formatCountPlayback(audio.player.currentTime));
//             setTrackProgress();
//             return false;
//         }

//         var prevPos = --state.currentPosition;
//         if (prevPos < 0) {
//             state.currentPosition = state.totalLength - 1;
//         } else {
//             state.currentPosition = prevPos;
//         }
//         loadAudio();
//     }

//     function playPauseAudio() {
//         if (!state.playing) {
//             $(controls.playPauseBtn).addClass(CSS_CLASSES.PLAYING);
//             audio.player.play();
//             startCountPlayback();
//         } else {
//             $(controls.playPauseBtn).removeClass(CSS_CLASSES.PLAYING);
//             audio.player.pause();
//             stopCountPlayback();
//         }
//         state.playing = !state.playing;
//     }

//     function stopAudio() {
//         state.playing = false;
//         audio.player.pause();
//     }



//     function muteAudio() {
//         if (!state.muted) {
//             audio.player.volume = 0;
//             state.currentVolume = 0;
//             $(controls.volume).val(0);
//         } else {
//             audio.player.volume = state.currentVolume;
//         }
//         setVolumeCSSClass();
//         state.muted = !state.muted;
//     }

//     function hightlightCurrentPlaylistItem() {
//         forEach(audio.playlist, function(element) {
//             $(element).removeClass(CSS_CLASSES.ACTIVE_ITEM);
//         });
//         $(audio.playlist[state.currentPosition]).addClass(CSS_CLASSES.ACTIVE_ITEM);
//     }

//     function setTrackProgress() {
//         progressBar.track.width(getTrackPercentage() + '%');
//     }

//     function setTrackPosition(xPos) {
//         var percentage = ( xPos / $(progressBar.container).width() ) * 100;
//         return parseInt((percentage / 100 ) * audio.itemDuration, 10);
//     }

//     function getTrackPercentage() {
//         var percentage = (audio.player.currentTime / audio.itemDuration) * 100;
//         return percentage;
//     }

//     return {
//         init: init
//     };
// });
